import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Flag, ShieldAlert, Loader2 } from "lucide-react";

/**
 * ReportModal (Compliance Roadmap Item 16)
 *
 * Reusable content-report form. Categories match the UGC Rules page and
 * the backend `REPORT_CATEGORIES` constant in `reports.py`. `target_type`
 * / `target_id` are supplied by the caller (the surface that mounts the
 * button — CardDetailPage, user profile, chat message, etc.).
 *
 * Only authenticated users can submit; anonymous visitors see a login
 * prompt instead of the form.
 */
export const REPORT_CATEGORIES = [
  { id: "csam_or_minor", label: "Sexualizes or depicts someone under 21", severity: "critical" },
  { id: "non_consent", label: "Non-consenting / revenge / deepfake", severity: "critical" },
  { id: "real_person_no_consent", label: "Real, identifiable person without consent", severity: "high" },
  { id: "doxxing", label: "Real-world identifying info about a person", severity: "high" },
  { id: "illegal_content", label: "Otherwise illegal where produced or viewed", severity: "high" },
  { id: "hate_or_threats", label: "Hate speech, threats, or harassment", severity: "high" },
  { id: "ip_infringement", label: "Copyright / trademark / right of publicity", severity: "medium" },
  { id: "malware_or_phish", label: "Malware, phishing, or security payload", severity: "medium" },
  { id: "other", label: "Something else — I'll describe it below", severity: "low" },
];

const SEVERITY_COLOR = {
  critical: "border-red-400/70 bg-red-500/[0.08] text-red-100",
  high: "border-amber-400/60 bg-amber-500/[0.06] text-amber-100",
  medium: "border-white/20 bg-white/[0.03] text-white/85",
  low: "border-white/15 bg-white/[0.02] text-white/75",
};

export function ReportModal({ open, onClose, targetType, targetId, contextLabel }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const detailsRequired = category === "other";

  const reset = () => { setCategory(""); setDetails(""); setSubmitting(false); };

  const submit = async () => {
    if (!category) { toast.error("Pick a category first."); return; }
    if (detailsRequired && !details.trim()) { toast.error("Please describe the issue."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        category,
        details: details.trim() || null,
        context_url: typeof window !== "undefined" ? window.location.pathname : null,
      });
      toast.success("Report submitted. Thanks — the moderation team will review it.");
      reset();
      onClose?.(res?.data);
    } catch (err) {
      toast.error(apiError(err) || "Could not submit your report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) { reset(); onClose?.(); } }}>
      <DialogContent className="bg-[hsl(220_18%_9%)] border-white/10 max-w-lg" data-testid="report-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white font-display tracking-[0.05em]">
            <ShieldAlert className="h-5 w-5 text-rose-300" /> Report this {targetType}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-[13px]">
            {contextLabel ? <>Reporting: <span className="text-white/85">{contextLabel}</span>. </> : null}
            Reports go to the moderation team. Bad-faith reports may result in enforcement against the reporter.
          </DialogDescription>
        </DialogHeader>
        {!user ? (
          <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-[13px] text-white/70">
            You need to be signed in to submit a report. Sign in and try again.
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mb-2">What&apos;s wrong?</div>
              <RadioGroup value={category} onValueChange={setCategory} className="space-y-1.5" data-testid="report-category-group">
                {REPORT_CATEGORIES.map((c) => (
                  <label
                    key={c.id}
                    htmlFor={`rc-${c.id}`}
                    data-testid={`report-category-${c.id}`}
                    className={`flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${category === c.id ? SEVERITY_COLOR[c.severity] : "border-white/[0.08] hover:border-white/20 bg-black/20 text-white/70"}`}
                  >
                    <RadioGroupItem value={c.id} id={`rc-${c.id}`} className="mt-0.5" />
                    <span className="text-[13px] leading-snug">{c.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                {detailsRequired ? "Describe the issue (required)" : "Additional detail (optional)"}
              </Label>
              <Textarea
                data-testid="report-details-input"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Where did you see this, what specifically violates the rule, any evidence links…"
                maxLength={2000}
                rows={3}
                className="mt-1.5 bg-[hsl(220_18%_12%)] border-white/10 text-white"
              />
              <div className="text-[10px] text-white/35 mt-1 font-mono text-right">{details.length}/2000</div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" data-testid="report-cancel-button" disabled={submitting} onClick={() => { reset(); onClose?.(); }} className="border-white/15 text-white">
            Cancel
          </Button>
          {!!user && (
            <Button
              data-testid="report-submit-button"
              disabled={submitting || !category || (detailsRequired && !details.trim())}
              onClick={submit}
              className="bg-rose-400 text-black hover:bg-rose-300 disabled:opacity-50"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Submitting…</> : <><Flag className="h-4 w-4 mr-1" /> Submit Report</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Convenience wrapper — renders a small "Report" text button that opens
 *  a `<ReportModal>` for the given target. Prefer this in listing rows,
 *  card details, chat messages, etc. */
export function ReportButton({ targetType, targetId, contextLabel, className = "", label = "Report" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`report-button-${targetType}-${targetId}`}
        className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 hover:text-rose-300 transition-colors ${className}`}
      >
        <Flag className="h-3 w-3" /> {label}
      </button>
      <ReportModal open={open} onClose={() => setOpen(false)} targetType={targetType} targetId={targetId} contextLabel={contextLabel} />
    </>
  );
}

export default ReportModal;
